import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from '@/components/brand/Icon';
import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-action text-on-accent hover:bg-action-hover active:bg-action-pressed border border-transparent',
  secondary:
    'bg-surface text-primary border border-border-strong hover:bg-subtle active:bg-border-subtle',
  tertiary:
    'bg-transparent text-primary border border-border-default hover:bg-subtle active:bg-border-subtle',
  ghost:
    'bg-transparent text-primary border border-transparent hover:bg-subtle active:bg-border-subtle',
  destructive:
    'bg-danger-fg text-on-solid border border-transparent hover:brightness-110 active:brightness-95',
};

const ON_MIDNIGHT: Partial<Record<ButtonVariant, string>> = {
  secondary:
    'bg-transparent text-on-midnight border border-border-on-midnight hover:bg-midnight-raised active:bg-midnight',
  tertiary: 'bg-transparent text-on-midnight border border-transparent hover:bg-midnight-raised',
  ghost: 'bg-transparent text-on-midnight border border-transparent hover:bg-midnight-raised',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-4 text-body-sm gap-1.5 rounded-sm',
  md: 'h-11 px-6 text-body gap-2 rounded-md',
  lg: 'h-13 px-8 text-body gap-2 rounded-md',
};

interface BaseProps {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  leadingIcon?: IconName | undefined;
  trailingIcon?: IconName | undefined;
  loading?: boolean | undefined;
  fullWidth?: boolean | undefined;
  onMidnight?: boolean | undefined;
  className?: string | undefined;
  children?: React.ReactNode | undefined;
}

function classesFor({
  variant = 'primary',
  size = 'md',
  fullWidth,
  onMidnight,
  className,
}: BaseProps): string {
  return cn(
    'inline-flex items-center justify-center font-medium whitespace-nowrap',
    'transition-[background-color,border-color,color,filter] duration-150',
    'disabled:pointer-events-none disabled:opacity-45',
    SIZES[size],
    (onMidnight && ON_MIDNIGHT[variant]) || VARIANTS[variant],
    fullWidth && 'w-full',
    className,
  );
}

type ButtonProps = BaseProps & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { children, leadingIcon, trailingIcon, loading, disabled, ...props },
  ref,
) {
  const { variant, size, fullWidth, onMidnight, className, ...rest } = props;
  const iconSize = size === 'sm' ? 16 : size === 'lg' ? 20 : 18;

  return (
    <button
      ref={ref}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={classesFor({ variant, size, fullWidth, onMidnight, className })}
      {...rest}
    >
      {loading ? (
        <Icon name="loader" size={iconSize} className="animate-spin" />
      ) : (
        leadingIcon && <Icon name={leadingIcon} size={iconSize} />
      )}
      {children}
      {trailingIcon && !loading && <Icon name={trailingIcon} size={iconSize} />}
    </button>
  );
});

type LinkButtonProps = BaseProps & {
  to: string;
  state?: unknown;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

export function LinkButton({
  to,
  children,
  leadingIcon,
  trailingIcon,
  state,
  ...props
}: LinkButtonProps): React.JSX.Element {
  const { variant, size, fullWidth, onMidnight, className, ...rest } = props;
  const iconSize = size === 'sm' ? 16 : size === 'lg' ? 20 : 18;

  return (
    <Link
      to={to}
      state={state}
      className={classesFor({ variant, size, fullWidth, onMidnight, className })}
      {...rest}
    >
      {leadingIcon && <Icon name={leadingIcon} size={iconSize} />}
      {children}
      {trailingIcon && <Icon name={trailingIcon} size={iconSize} />}
    </Link>
  );
}

export function IconButton({
  icon,
  label,
  variant = 'ghost',
  size = 'md',
  onMidnight,
  className,
  ...rest
}: {
  icon: IconName;
  label: string;
} & Omit<ButtonProps, 'children' | 'leadingIcon' | 'trailingIcon'>): React.JSX.Element {
  const box = size === 'sm' ? 'size-9' : size === 'lg' ? 'size-13' : 'size-11';

  return (
    <button
      type="button"
      aria-label={label}
      className={cn(
        'inline-flex items-center justify-center rounded-md transition-colors duration-150',
        'disabled:pointer-events-none disabled:opacity-45',
        box,
        (onMidnight && ON_MIDNIGHT[variant]) || VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      <Icon name={icon} size={size === 'sm' ? 16 : 20} />
    </button>
  );
}
